import { MEMBERS_AREA, MEMBERS_AREA_V2 } from '@wix/app-definition-ids';
import type { ComponentRef } from '@wix/platform-editor-sdk';
import type { FlowEditorSDK } from '@wix/yoshi-flow-editor';

import {
  APP_TOKEN,
  COMPONENTS_SECTION,
  COMPONENTS_SECTION_RESPONSIVE,
  Experiment,
  MEMBER_SETTINGS_TPA_PAGE_ID,
  MEMBER_TPA_PAGE_ID,
} from '../../constants';
import type { PlatformContext } from '../../types';
import { getIsEditor3 } from '../../utils';
import { breakpoints } from '../definitions/breakpoints';
import { getChildren, getType } from '../editor-sdk-wrappers';
import { runAndWaitForApproval } from '../editor-sdk-wrappers/document';
import {
  addPage,
  renamePage,
  updatePageData,
} from '../editor-sdk-wrappers/pages';
import { globalAppState } from './global-app-state';
import { updateMenusMembersPath } from './menus-wrapper';

export const createSettingsPage = async ({
  editorSDK,
  flowAPI,
}: Pick<PlatformContext, 'flowAPI' | 'editorSDK'>) => {
  const { isEditorX } = flowAPI.environment;
  // isEditorX is true even when it's actually E3
  const isEditor3 = getIsEditor3();

  const action = async () => {
    return addPage(editorSDK, {
      title: flowAPI.translations.t('app.page.settings.title'),
      definition: {
        id: '',
        type: 'Page',
        data: {
          appDefinitionId: MEMBERS_AREA_V2,
          managingAppDefId: MEMBERS_AREA,
          tpaPageId: MEMBER_SETTINGS_TPA_PAGE_ID,
          pageUriSEO: 'account-settings',
          pageSecurity: { requireLogin: true },
          indexable: false,
        },
        componentType: 'mobile.core.components.Page',
        breakpoints: isEditorX && !isEditor3 ? breakpoints : undefined,
      },
      shouldAddMenuItem: false,
      shouldNavigateToPage: false,
    });
  };

  return runAndWaitForApproval(editorSDK, action);
};

type CreateProfilePageOptions = Pick<
  PlatformContext,
  'editorSDK' | 'flowAPI'
> & { title: string; pageUriSEO: string };

export const createProfilePage = async ({
  editorSDK,
  flowAPI,
  title,
  pageUriSEO,
}: CreateProfilePageOptions) => {
  const avoidNavigation =
    globalAppState.getIsClassicEditor() ||
    flowAPI.experiments.enabled(Experiment.RemoveUpdatingBoBController);
  const { isEditorX } = flowAPI.environment;
  // isEditorX is true even when it's actually E3
  const isEditor3 = getIsEditor3();

  return runAndWaitForApproval(editorSDK, () =>
    addPage(editorSDK, {
      title,
      definition: {
        id: '',
        type: 'Page',
        data: {
          appDefinitionId: MEMBERS_AREA_V2,
          managingAppDefId: MEMBERS_AREA,
          tpaPageId: MEMBER_TPA_PAGE_ID,
          pageUriSEO,
          indexable: true,
        },
        componentType: 'mobile.core.components.Page',
        breakpoints: isEditorX && !isEditor3 ? breakpoints : undefined,
      },
      shouldAddMenuItem: false,
      shouldNavigateToPage: !avoidNavigation,
    }),
  );
};

const getProfilePageData = async (editorSDK: FlowEditorSDK) => {
  const pagesData = await editorSDK.document.pages.getApplicationPages('', {
    includeUnmanaged: true,
  });
  const pageData = pagesData.find(
    ({ tpaPageId }) => tpaPageId === MEMBER_TPA_PAGE_ID,
  );

  if (pageData) {
    return pageData;
  }

  throw new Error('[MAV2] Members Area page not found');
};

export const getPageRefByTPAPageId = (
  editorSDK: FlowEditorSDK,
  tpaPageId: string,
) => {
  return editorSDK.document.tpa.getPageRefByTPAPageId('', {
    tpaPageId,
  });
};

export const getSettingsPageRef = async (editorSDK: FlowEditorSDK) => {
  return getPageRefByTPAPageId(editorSDK, MEMBER_SETTINGS_TPA_PAGE_ID);
};

export const getMembersAreaPageRef = async (editorSDK: FlowEditorSDK) => {
  return getPageRefByTPAPageId(editorSDK, MEMBER_TPA_PAGE_ID);
};

export async function getMembersAreaPageId(editorSDK: FlowEditorSDK) {
  const membersAreaPageRef = await getMembersAreaPageRef(editorSDK);
  const membersAreaPage = await editorSDK.pages.data.get(APP_TOKEN, {
    pageRef: membersAreaPageRef,
  });

  if (!membersAreaPage?.id) {
    throw new Error('Members Area main page id is not found');
  }

  return membersAreaPage.id;
}

export const isMemberPageInstalled = async (editorSDK: FlowEditorSDK) =>
  !!(await getMembersAreaPageRef(editorSDK));

export const isSettingsPageInstalled = async (editorSDK: FlowEditorSDK) =>
  !!(await getSettingsPageRef(editorSDK));

export const renameMembersAreaPage = async (
  editorSDK: FlowEditorSDK,
  newPageTitle: string,
) => {
  const pageRef = await getMembersAreaPageRef(editorSDK);
  if (!pageRef) {
    throw new Error('[MAV2] Members Area page ref not found');
  }
  const { title } = await getProfilePageData(editorSDK);

  if (newPageTitle !== title) {
    return renamePage(editorSDK, { pageRef, title: newPageTitle });
  }
};

export const updatePageUriSEO = async (
  editorSDK: FlowEditorSDK,
  newPageUriSEO: string,
) => {
  const { id, pageUriSEO } = await getProfilePageData(editorSDK);
  if (newPageUriSEO === pageUriSEO) {
    return;
  }

  await Promise.all([
    updateMenusMembersPath(editorSDK, pageUriSEO, newPageUriSEO),
    updatePageData(editorSDK, {
      pageRef: { id: id!, type: 'DESKTOP' },
      data: { pageUriSEO: newPageUriSEO },
    }),
  ]);
};

export const findSectionInPage = async (
  { editorSDK, flowAPI }: Pick<PlatformContext, 'editorSDK' | 'flowAPI'>,
  pageRef: ComponentRef,
) => {
  const pageChildren = await getChildren(editorSDK, pageRef);
  const types = await Promise.all(
    pageChildren.map((childRef) => getType(editorSDK, childRef)),
  );
  const sectionType = flowAPI.environment.isEditorX
    ? COMPONENTS_SECTION_RESPONSIVE
    : COMPONENTS_SECTION;
  const sectionIndex = types.findIndex((type) => type === sectionType);

  return pageChildren[sectionIndex];
};
